/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import type { EuiButtonProps } from '@elastic/eui'
import { EuiButton, EuiContextMenuItem, EuiContextMenuPanel, EuiPopover } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { RulesetType } from '@modules/network-security-lib/types'

type Props = Pick<EuiButtonProps, 'fill' | 'size'> & {
  onClick: (rulesetType: RulesetType) => void
}

const ApplyButton: React.FunctionComponent<Props> = ({ fill, size, onClick }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { formatMessage } = useIntl()

  const items = [
    <EuiContextMenuItem
      key='ipFilter'
      icon='logstashFilter'
      onClick={() => {
        onClick('ipFilter')
        setIsPopoverOpen(false)
      }}
    >
      <FormattedMessage id='networkSecurity.applyIpFilter' defaultMessage='IP filter' />
    </EuiContextMenuItem>,
    <EuiContextMenuItem
      key='privateConnection'
      icon='lock'
      onClick={() => {
        onClick('privateConnection')
        setIsPopoverOpen(false)
      }}
    >
      <FormattedMessage
        id='networkSecurity.applyPrivateConnection'
        defaultMessage='Private connection'
      />
    </EuiContextMenuItem>,
  ]

  const button = (
    <EuiButton
      data-test-id='trafficFilter.applyButton'
      color='primary'
      size={size}
      fill={fill}
      iconType='arrowDown'
      iconSide='right'
      onClick={() => {
        setIsPopoverOpen(!isPopoverOpen)
      }}
      aria-label={formatMessage({
        id: 'networkSecurity.applyButton',
        defaultMessage: 'Apply policies',
      })}
    >
      <FormattedMessage id='networkSecurity.applyButton' defaultMessage='Apply policies' />
    </EuiButton>
  )

  // the div is necessary to make sure the anchor position is correct
  return (
    <div>
      <EuiPopover
        id='applyTrafficFilterPopover'
        button={button}
        isOpen={isPopoverOpen}
        closePopover={() => {
          setIsPopoverOpen(false)
        }}
        panelPaddingSize='none'
        anchorPosition='downLeft'
      >
        <EuiContextMenuPanel items={items} />
      </EuiPopover>
    </div>
  )
}

export default ApplyButton
